import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { apiClient } from '@services/apiClient';

import type { ILocationAsset } from '../model/locationAsset';

interface IOptions {
  searchValue?: string;
  locationId?: number | string;
  onlyWithActionItem?: number;
  actionItemType?: string;
  actionItemUrgencyUi?: string;
}

interface IResponse {
  data: Array<ILocationAsset>;
}

interface ISensorsLocations {
  facilityIds: Array<number>;
  locationIds: { [key: string]: boolean };
}

export const locationsAssetsKey = (options?: IOptions) => [
  'locationsAssets',
  options?.searchValue,
  options?.locationId,
  options?.onlyWithActionItem,
  options?.actionItemType,
  options?.actionItemUrgencyUi,
];

export const locationsAssetsFn = ({ queryKey }: QueryFunctionContext<(string | number)[]>) => {
  const query = queryKey[1];
  const actionItemType = queryKey[4];
  const actionItemUrgency = queryKey[5];

  const params = {
    filters: {
      query,
      locationId: queryKey[2],
      onlyWithActionItem: queryKey[3],
      actionItemTypeUi: actionItemType !== 'all' ? actionItemType : undefined,
      actionItemUrgencyUi: actionItemUrgency !== 'all' ? actionItemUrgency : undefined,
    },
  };

  return apiClient.get(`${APP_API_URL_V2}fsd/locations/assets`, {
    params,
  });
};

const locationsAssetsSelect = (data: IResponse, searchValue?: string) => {
  const assetsList = data.data || [];
  if (!searchValue) {
    return { assetsList, sensorsLocations: { facilityIds: [], locationIds: {} } };
  }

  const sensorsLocations: ISensorsLocations = {
    facilityIds: [],
    locationIds: {},
  };

  assetsList.forEach((loc) => {
    sensorsLocations.locationIds[loc.id] = true;
    sensorsLocations.locationIds[loc.parentIds[0]] = true;
    sensorsLocations.facilityIds.push(loc.parentIds[0]);
  });

  return { assetsList, sensorsLocations };
};

export const useLocationsAssetsQuery = (options: IOptions) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: locationsAssetsKey(options),
    queryFn: locationsAssetsFn,
    select: ({ data }: AxiosResponse<IResponse>) => locationsAssetsSelect(data, options.searchValue),
    enabled: !!options.locationId || !!options.searchValue,
  });
};
